import { useAnalytics } from '@repo/analytics/analytics-provider';
import { IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { Tooltip } from '@repo/design-system/components/ds/tooltip/tooltip';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import type { LiveLocationCtaSource } from '@repo/types/analytic-events/maps-analytic-events';
import { HostType } from '@repo/types/host';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePlayerLocation } from '../game-map/hooks/usePlayerLocation';
import { useMapContext } from '../game-map/map-context';
import { PlayerLocationMarker } from '../player-location-marker/player-location-marker';
const ATTENTION_DELAY_MS = 5000;
const ATTENTION_DURATION_MS = 1500;
const ATTENTION_COLOR_FLIP_MS = 1330;
const HEADING_CONE_KEYFRAMES = `
@keyframes wandLocateHeadingConeArrow {
  0%,9% { transform: rotate(0); }
  22%,34% { transform: rotate(132deg); }
  46%,58% { transform: rotate(-104deg); }
  70%,84% { transform: rotate(42deg); }
  100% { transform: rotate(0); }
}
@keyframes wandLocateHeadingConeCone {
  0% { opacity: 0; transform: rotate(45deg); }
  9% { opacity: 1; transform: rotate(45deg); }
  22%,34% { transform: rotate(177deg); }
  46%,58% { transform: rotate(-59deg); }
  70%,84% { transform: rotate(87deg); }
  90% { opacity: 1; transform: rotate(45deg); }
  100% { opacity: 0; transform: rotate(45deg); }
}`;
const ATTENTION_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
export interface LiveLocationCtaControl {
  attentionKey: string | null;
  attentionSuppressed: boolean;
  open: boolean;
  onOpen: (source: LiveLocationCtaSource) => void;
  onHover: (source: LiveLocationCtaSource) => void;
  onAttentionAnimationShown: () => void;
}
interface PlayerLocationButtonProps {
  mapId: string;
  hidden?: boolean;
  liveLocationCta?: LiveLocationCtaControl;
}
export const PlayerLocationButton = ({
  mapId,
  hidden = false,
  liveLocationCta
}: PlayerLocationButtonProps) => {
  const {
    map
  } = useMapContext();
  const hostType = useHostType();
  const analytics = useAnalytics();
  const {
    t
  } = useT(['game_map']);
  const [showAttention, setShowAttention] = useState(false);
  const [attentionPlaying, setAttentionPlaying] = useState(false);
  const attentionShownRef = useRef(false);
  const attentionKeyRef = useRef<string | null | undefined>(undefined);
  const attentionTimersRef = useRef<number[]>([]);
  const attentionDelayRef = useRef<number | undefined>(undefined);

  // Timers live in a ref (not the effect) so re-renders can't cancel an in-flight animation.
  const clearAttentionTimers = useCallback(() => {
    for (const id of attentionTimersRef.current) {
      window.clearTimeout(id);
    }
    attentionTimersRef.current = [];
  }, []);

  // Kept separate from clearAttentionTimers: the attention-key reset must not cancel a pending
  // delay (the scheduling effect's deps don't change on key switch, so it would never reschedule).
  const clearAttentionDelay = useCallback(() => {
    window.clearTimeout(attentionDelayRef.current);
    attentionDelayRef.current = undefined;
  }, []);
  const {
    followEnabled,
    toggleFollow,
    setFollow,
    supportsLiveLocation,
    locationEnabled
  } = usePlayerLocation();
  const isLiveLocationCta = hostType === HostType.WEB && Boolean(liveLocationCta);
  const liveLocationCtaAttentionKey = liveLocationCta?.attentionKey;
  const attentionSuppressed = liveLocationCta?.attentionSuppressed ?? true;

  // Ref-held so the scheduling effect depends on stable primitives only; otherwise the churning
  // control object keeps resetting the ~5s timer and the animation never fires.
  const onAttentionAnimationShownRef = useRef(liveLocationCta?.onAttentionAnimationShown);
  onAttentionAnimationShownRef.current = liveLocationCta?.onAttentionAnimationShown;
  useEffect(() => {
    if (!map) return;
    const stopFollow = () => setFollow(false);
    map.on('dragstart', stopFollow);
    return () => {
      map.off('dragstart', stopFollow);
    };
  }, [map, setFollow]);
  useEffect(() => {
    if (attentionKeyRef.current === liveLocationCtaAttentionKey) return;
    attentionKeyRef.current = liveLocationCtaAttentionKey;
    attentionShownRef.current = false;
    clearAttentionTimers();
    setShowAttention(false);
    setAttentionPlaying(false);
  }, [clearAttentionTimers, liveLocationCtaAttentionKey]);
  useEffect(() => {
    if (!isLiveLocationCta || attentionSuppressed || !map || hidden || attentionShownRef.current) {
      return;
    }
    let disposed = false;
    const playAttention = () => {
      attentionDelayRef.current = window.setTimeout(() => {
        if (disposed || attentionShownRef.current) return;
        attentionShownRef.current = true;
        onAttentionAnimationShownRef.current?.();
        const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
        if (prefersReducedMotion) {
          setShowAttention(true);
          return;
        }
        setAttentionPlaying(true);
        attentionTimersRef.current.push(window.setTimeout(() => setShowAttention(true), ATTENTION_COLOR_FLIP_MS));
        attentionTimersRef.current.push(window.setTimeout(() => setAttentionPlaying(false), ATTENTION_DURATION_MS));
      }, ATTENTION_DELAY_MS);
    };
    if (map.loaded()) {
      playAttention();
    } else {
      map.once('idle', playAttention);
    }
    return () => {
      disposed = true;
      clearAttentionDelay();
      map.off('idle', playAttention);
    };
  }, [attentionSuppressed, clearAttentionDelay, hidden, isLiveLocationCta, map]);
  useEffect(() => clearAttentionTimers, [clearAttentionTimers]);
  const liveLocationCtaOpen = liveLocationCta?.open ?? false;
  useEffect(() => {
    if (liveLocationCtaOpen) {
      clearAttentionDelay();
      clearAttentionTimers();
      setShowAttention(false);
      setAttentionPlaying(false);
    }
  }, [clearAttentionDelay, clearAttentionTimers, liveLocationCtaOpen]);
  const tooltipContent = isLiveLocationCta ? t($ => $.live_location_cta.button_tooltip) : hostType !== HostType.APP ? t($_0 => $_0.player_location_web_wand) : supportsLiveLocation ? t($_1 => $_1.player_location_desktop) : t($_2 => $_2.player_location_desktop_disabled_wand);
  const trackPlayerLocationButtonClick = (enabled: boolean, action: 'toggle_follow' | 'open_live_location_cta') => {
    if (analytics && mapId) {
      analytics.event('game_map_player_location_button_click', {
        mapId,
        enabled,
        action
      });
    }
  };
  const handleToggleFollow = () => {
    toggleFollow();
    trackPlayerLocationButtonClick(!followEnabled, 'toggle_follow');
  };
  const handleClick = () => {
    if (isLiveLocationCta && liveLocationCta) {
      clearAttentionDelay();
      clearAttentionTimers();
      setShowAttention(false);
      setAttentionPlaying(false);
      trackPlayerLocationButtonClick(followEnabled, 'open_live_location_cta');
      liveLocationCta.onOpen('locate_button');
      return;
    }
    handleToggleFollow();
  };
  const handleMouseEnter = () => {
    if (isLiveLocationCta && liveLocationCta) {
      liveLocationCta.onHover('locate_button');
    }
  };
  return <>
            {supportsLiveLocation && <div className={css({
      visibility: hidden ? 'hidden' : 'visible'
    })}>
                    {isLiveLocationCta &&
      // biome-ignore lint/security/noDangerouslySetInnerHtml: static, self-authored keyframes.
      <style dangerouslySetInnerHTML={{
        __html: HEADING_CONE_KEYFRAMES
      }} />}
                    <Tooltip content={tooltipContent} offset={8} placement="top" size="md">
                        <IconButton onClick={handleClick} onMouseEnter={handleMouseEnter} color="white" size="lg" disabled={!isLiveLocationCta && !locationEnabled} aria-label={tooltipContent} className={css({
          color: !isLiveLocationCta && followEnabled ? 'purple.600' : 'black',
          backgroundColor: showAttention ? 'brand.primary' : isLiveLocationCta ? '[#f0f5f5]' : undefined,
          borderRadius: 'full',
          '&:not(:focus-visible)': {
            boxShadow: 'lg'
          },
          position: isLiveLocationCta ? 'relative' : undefined,
          isolation: isLiveLocationCta ? 'isolate' : undefined,
          overflow: isLiveLocationCta ? 'visible' : undefined,
          transition: '[background-color 450ms cubic-bezier(0.16, 1, 0.3, 1)]',
          '@media (prefers-reduced-motion: reduce)': {
            transition: '[none]'
          }
        })}>
                            {!isLiveLocationCta && <Icon icon="near_me" decorative fill />}
                            {isLiveLocationCta && <>
                                    {/* Cone sits below the face occluder so it reads as emerging from under the rim. */}
                                    <span aria-hidden="true" className={css({
              position: 'absolute',
              inset: '[-44px]',
              zIndex: 0,
              opacity: 0,
              pointerEvents: 'none',
              transformOrigin: 'center',
              background: '[radial-gradient(closest-side, rgba(231, 255, 12, 0.72) 36%, rgba(231, 255, 12, 0) 100%)]',
              clipPath: '[polygon(63.2px 64px, 72.8px 64px, 110px 4px, 26px 4px)]'
            })} style={attentionPlaying ? {
              animation: `wandLocateHeadingConeCone ${ATTENTION_DURATION_MS}ms ${ATTENTION_EASE} forwards`
            } : undefined} />
                                    <span aria-hidden="true" className={css({
              position: 'absolute',
              inset: '0',
              zIndex: 1,
              borderRadius: 'full',
              backgroundColor: '[inherit]',
              pointerEvents: 'none'
            })} />
                                    <span className={css({
              position: 'relative',
              zIndex: 2,
              display: 'grid',
              placeItems: 'center',
              transformOrigin: 'center'
            })} style={attentionPlaying ? {
              animation: `wandLocateHeadingConeArrow ${ATTENTION_DURATION_MS}ms ${ATTENTION_EASE} forwards`
            } : undefined}>
                                        <Icon icon="near_me" fill size="md" decorative />
                                    </span>
                                </>}
                        </IconButton>
                    </Tooltip>
                </div>}
            {supportsLiveLocation && locationEnabled && !isLiveLocationCta && <PlayerLocationMarker />}
        </>;
};